import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import type { GroupKPIMeta, GroupMapPoint, GroupTerritories, Tier } from '../../api/groupSteering'
import { useLocale } from '../../contexts/LocaleContext'
import { TIER_TEXT, TIER_VAR, formatUnit, langOf, token } from './format'
import { EmptyState } from '../ui/empty-state'

const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const TILE_ATTRIBUTION = '&copy; OpenStreetMap contributors'

/**
 * The portfolio map (spec 2026-09-11-01, "Implantation"): one point per
 * geocoded site, sized by its units, tinted by its tier on the current
 * indicator, fitted to the parc's extent — a group present in three
 * countries sees three countries. Plain circle markers, no clustering
 * dependency; a click opens the site.
 */
export function PortfolioMap({ points, kpi, org, selectedPerimeter }: { points: GroupMapPoint[]; kpi: GroupKPIMeta; org: string; selectedPerimeter?: string }) {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const mapRef = useRef<L.Map | null>(null)
    const layerRef = useRef<L.LayerGroup | null>(null)
    const navigate = useNavigate()
    const { locale } = useLocale()
    const lang = langOf(locale)

    useEffect(() => {
        if (!containerRef.current || mapRef.current) return
        const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true, scrollWheelZoom: false })
        L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 18 }).addTo(map)
        map.setView([46.6, 2.4], 5)
        mapRef.current = map
        layerRef.current = L.layerGroup().addTo(map)
        return () => {
            map.remove()
            mapRef.current = null
            layerRef.current = null
        }
    }, [])

    useEffect(() => {
        const map = mapRef.current
        const layer = layerRef.current
        if (!map || !layer) return
        layer.clearLayers()
        const bounds: L.LatLngExpression[] = []
        for (const p of points) {
            const tier = (p.tier as Tier) || 'none'
            const color = token(TIER_VAR[tier], '#6b7280')
            const radius = Math.max(6, Math.min(22, 4 + Math.sqrt(p.units || 1) * 1.6))
            const selected = !!selectedPerimeter && p.perimeter?.uid === selectedPerimeter
            const marker = L.circleMarker([p.lat, p.lng], {
                radius,
                color: selected ? token('--foreground', '#111') : color,
                weight: selected ? 3 : 1.5,
                fillColor: color,
                fillOpacity: 0.55,
            })
            const value = p.value === null || p.value === undefined ? '—' : formatUnit(p.value, kpi.unit, lang, locale)
            marker.bindTooltip(`<strong>${p.name}</strong><br/>${p.units} · ${p.perimeter?.name ?? '—'}<br/>${value}`, { direction: 'top' })
            marker.on('click', () => navigate({ to: '/$org/assets/$assetUid', params: { org, assetUid: p.asset_uid } }))
            marker.addTo(layer)
            bounds.push([p.lat, p.lng])
        }
        if (bounds.length > 0) {
            map.fitBounds(L.latLngBounds(bounds), { padding: [24, 24], maxZoom: 12 })
        }
    }, [points, kpi, lang, locale, navigate, org, selectedPerimeter])

    return <div ref={containerRef} className="h-[360px] w-full rounded-md border overflow-hidden" data-testid="portfolio-map" />
}

/** The country › subdivision table, pattern §7. */
export function TerritoryTable({ territories, kpi }: { territories: GroupTerritories; kpi: GroupKPIMeta }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    if (territories.rows.length === 0)
        return <EmptyState variant="inline" title={t('consolidated.map_not_geocoded', { count: 0 })} />
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="territory-table">
                <thead className="text-xs text-muted-foreground">
                    <tr className="text-left">
                        <th className="py-1 pr-2 font-medium">ISO</th>
                        <th className="py-1 pr-2 font-medium">{t('consolidated.map_territories')}</th>
                        <th className="py-1 pr-2 font-medium text-right">{t('governance.sites')}</th>
                        <th className="py-1 pr-2 font-medium text-right">{t('governance.units')}</th>
                        <th className="py-1 pr-2 font-medium text-right">{kpi.label ? (kpi.label[lang] ?? kpi.label.en) : ''}</th>
                        <th className="py-1 pr-2 font-medium text-right">n</th>
                        <th className="py-1 font-medium text-right">{t('tier.watch')}</th>
                    </tr>
                </thead>
                <tbody>
                    {territories.rows.map((r) => (
                        <tr key={`${r.country}/${r.admin_area_1 ?? ''}`} className="border-t" data-testid={`territory-row-${r.country}-${(r.admin_area_1 ?? '').replace(/\s+/g, '_')}`}>
                            <td className="py-1.5 pr-2 font-mono text-xs text-muted-foreground">{r.country}</td>
                            <td className="py-1.5 pr-2 text-foreground">{r.admin_area_1 || '—'}</td>
                            <td className="py-1.5 pr-2 text-right tabular-nums">{r.sites}</td>
                            <td className="py-1.5 pr-2 text-right tabular-nums">{r.units}</td>
                            <td className={`py-1.5 pr-2 text-right tabular-nums font-medium ${TIER_TEXT[(r.tier as Tier) || 'none']}`}>
                                {r.suppressed ? '—' : formatUnit(r.value, kpi.unit, lang, locale)}
                            </td>
                            <td className="py-1.5 pr-2 text-right tabular-nums text-muted-foreground">{r.n}</td>
                            <td className="py-1.5 text-right tabular-nums">{r.watch_count}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
